import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { contactApi, settingsApi } from '../../shared/api/endpoints';
import { CONTACT_DEFAULTS } from '../../shared/constants/contactDefaults';
import { formatPhoneDisplay } from '../../shared/utils/whatsapp';
import ValidatedInput, { validateFields } from '../../../validations/ValidatedInput';
import './ContactSection.css';

export default function ContactSection() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [status, setStatus] = useState({ type: '', text: '' });
  const [sending, setSending] = useState(false);
  const [displayPhone, setDisplayPhone] = useState('');
  const [contactInfo, setContactInfo] = useState({
    email: CONTACT_DEFAULTS.email,
    hours: CONTACT_DEFAULTS.hours,
    address: CONTACT_DEFAULTS.address,
  });

  useEffect(() => {
    settingsApi
      .get()
      .then((res) => {
        const data = res.data?.data || {};
        const num = data?.whatsapp?.number;
        if (num) setDisplayPhone(formatPhoneDisplay(num));
        setContactInfo({
          email: data.contactEmail || CONTACT_DEFAULTS.email,
          hours: data.contactHours || CONTACT_DEFAULTS.hours,
          address: data.contactAddress || CONTACT_DEFAULTS.address,
        });
      })
      .catch(() => {});
  }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const errs = validateFields([
      { kind: 'text', value: form.name, required: true, label: 'Name' },
      { kind: 'email', value: form.email, required: true },
      { kind: 'phone', value: form.phone, required: false },
      { kind: 'text', value: form.subject, required: false, label: 'Subject' },
      { kind: 'textarea', value: form.message, required: true, label: 'Message' },
    ]);
    if (errs.length) {
      setStatus({ type: 'err', text: errs[0] });
      return;
    }
    setSending(true);
    setStatus({ type: '', text: '' });
    try {
      const res = await contactApi.submit(form);
      setStatus({ type: 'ok', text: res.data.message || 'Message sent!' });
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      setStatus({
        type: 'err',
        text: err.response?.data?.message || 'Could not send. Try again.',
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="section contact section--lite" id="contact">
      <div className="contact__peacock" aria-hidden>
        <svg viewBox="0 0 400 400" className="contact__fan">
          {Array.from({ length: 15 }).map((_, i) => {
            const angle = -95 + (i * 190) / 14;
            const rad = (angle * Math.PI) / 180;
            const x = 200 + Math.cos(rad) * 150;
            const y = 240 + Math.sin(rad) * 150;
            return (
              <g key={i} opacity={0.35 + (i % 3) * 0.1}>
                <line x1="200" y1="240" x2={x} y2={y} stroke="#c9a227" strokeWidth="1.2" />
                <ellipse cx={x} cy={y} rx="10" ry="14" fill="none" stroke="#c2187a" strokeWidth="1.5" />
                <circle cx={x} cy={y - 2} r="3" fill="#7b4bb7" />
              </g>
            );
          })}
        </svg>
      </div>

      <div className="container contact__layout">
        <div>
          <p className="section-eyebrow">Reach us</p>
          <h2 className="section-title">Contact Us</h2>
          <p className="section-sub">
            Questions on fits, bridal timelines, or custom peacock motifs? Whisper to us —
            we reply with grace.
          </p>
          <ul className="contact__meta">
            {contactInfo.email && <li>{contactInfo.email}</li>}
            {displayPhone && <li>{displayPhone}</li>}
            {contactInfo.address && <li>{contactInfo.address}</li>}
            {contactInfo.hours && <li>{contactInfo.hours}</li>}
          </ul>
        </div>

        <motion.form
          className="contact__form"
          onSubmit={onSubmit}
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
        >
          <div className="contact__row">
            <ValidatedInput kind="text" name="name" placeholder="Name" value={form.name} onChange={onChange} required />
            <ValidatedInput kind="email" name="email" placeholder="Email" value={form.email} onChange={onChange} required />
          </div>
          <div className="contact__row">
            <ValidatedInput kind="phone" name="phone" placeholder="10-digit phone" value={form.phone} onChange={onChange} />
            <ValidatedInput kind="text" name="subject" placeholder="Subject" value={form.subject} onChange={onChange} />
          </div>
          <ValidatedInput
            kind="textarea"
            name="message"
            placeholder="Your message"
            rows={5}
            value={form.message}
            onChange={onChange}
            required
          />
          <button type="submit" className="btn btn-gold" disabled={sending}>
            {sending ? 'Sending…' : 'Send message'}
          </button>
          {status.text && (
            <p className={`contact__status contact__status--${status.type}`}>{status.text}</p>
          )}
        </motion.form>
      </div>
    </section>
  );
}
